import {
  Button,
  Center,
  Flex,
  Spacer,
  Text,
  UseDisclosureReturn,
  VStack,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/nextjs';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { useContext, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { useI18nLn, usePageError } from '~/hooks/store';
import { GachaSalesStatusDisplayButton } from '../common/buttons/GachaSalesStatusDisplayButton';
import { GpsCheckinModal } from '../common/modals/GpsCheckinModal';
import { TippingMessageForm } from '../message/TippingMessageForm';
import { NoShipmentSwitch } from './NoShipmentSwitch';
import { SelectDelivery } from './SelectDelivery';
import type { RequestOrderSubmitInput } from './SelectDetail';
import { SelectProductInfo } from './SelectProductInfo';
import { SelectWarning } from './SelectWarning';

import { ERROR_DESCRIPTIONS } from '@roll-web/constants';
import { SelectPaymentMethodContainer } from '~/components/select/SelectPaymentMethodContainer';
import { MESSAGE_MAX_LENGTH } from '~/constants/tipping_message';
import { usePlayTickets } from '~/hooks/resources';
import { useCreditList } from '~/hooks/resources/credit_list';
import { apiClient } from '~/lib/apiClient';
import { PAYMENT_METHOD_KV } from '~/lib/payment';
import { calculateBundleAmount } from '~/lib/utils/calculate_bundle_amounts';
import { calculatePaymentAmounts } from '~/lib/utils/calculate_payment_amounts';
import { RootContext } from '~/store';
import { PaymentMethodVariables } from '~/types/payment';
import type { PlayTicket } from '~/types/resources';
import { ApiPaymentMethod } from '~/types/resources/apiPaymentMethod';
import { DeliveryBundle } from '~/types/resources/deliveryBundle';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import type { GachaDetail } from '~/types/resources/gachaDetail';

type Props = {
  isSubmitting: boolean;
  canDeliveryInfoEdit: boolean;
  playAmount: number;
  playAmountPlans: number[];
  gachaDetail: GachaDetail;
  gpsCheckinDisclosure: UseDisclosureReturn;
  deliveryBundles: DeliveryBundle[];
  gachaPaymentMethods: ApiPaymentMethod[];
  deliveryInfoOnOpen: () => void;
  onOrderSubmit: (input: RequestOrderSubmitInput) => Promise<void>;
  paymentRedirectUrl: string | null;
};

// 購入に必要なユーザが入力する必要のある情報を入力するフォーム群
export const PlayGachaFormContainer: React.FC<Props> = (props: Props) => {
  const router = useRouter();
  const { token } = useContext(RootContext);
  const { filterPlayTicketsByGachaId } = usePlayTickets();
  const { setPageError } = usePageError();
  const credit = useCreditList();
  const { t } = useI18nLn();
  const isLocalPickOnly = useMemo(
    () => props.gachaDetail.delivery_type != 1,
    [props.gachaDetail.delivery_type]
  );
  const filteredByPlayAmountPlayTickets = useMemo(() => {
    return filterPlayTicketsByGachaId(props.gachaDetail.id).filter((ticket) => {
      return ticket.play_amount == props.playAmount;
    });
  }, [filterPlayTicketsByGachaId, props.gachaDetail.id, props.playAmount]);

  const deliveryForm = useFormContext<DeliveryInfo>();

  const canDeliverableForRegisterAddress: boolean = useMemo(() => {
    if (deliveryForm.watch('country') == undefined) return true;
    if (deliveryForm.watch('country') == 'JP') return true;
    return (
      props.gachaDetail.shipping_specs.findIndex(
        (spec) => spec.country === deliveryForm.watch('country')
      ) > -1
    );
  }, [deliveryForm, props.gachaDetail.shipping_specs]);

  const tippingMessageForm = useForm<{ tippingMessage: string }>();
  const deliveryFormValues = deliveryForm.watch();

  const isExistingPrecaution = useMemo(() => {
    const precautions = props.gachaDetail.purchase_precaution;
    return precautions != null && precautions.length > 0;
  }, [props.gachaDetail.purchase_precaution]);
  const [isConfirmedWarningText, setToConfirmWarningText] = useState(false);
  const [payment_method, setPaymentMethod] =
    useState<PaymentMethodVariables | null>(null);
  const [card_seq, setCardSeq] = useState<string | null>(null);
  const [selectedPlayTicket, setPlayTicket] = useState<PlayTicket | null>(null);
  // 配送しない設定(no_require_delivery_info)
  const [shouldNotDeliver, setShouldNotDeliver] = useState(false);

  const shippingSpec = useMemo(() => {
    if (deliveryFormValues == null) return;
    const country = deliveryFormValues.country;
    return props.gachaDetail.shipping_specs.find((spec) => {
      return spec.country == country;
    });
  }, [props.gachaDetail.shipping_specs, deliveryFormValues?.country]);

  const bundleAmount = calculateBundleAmount({
    playAmount: props.playAmount,
    deliveryBundles: props.deliveryBundles,
    deliveryInfo: deliveryFormValues,
    shippingSpec: shippingSpec,
  });

  const paymentAmounts = calculatePaymentAmounts({
    isPlayTicketPayment: selectedPlayTicket != null,
    skipDelivery: shouldNotDeliver,
    bundleAmount: bundleAmount,
    playAmount: props.playAmount,
    gachaDetail: props.gachaDetail,
    shippingSpec: shippingSpec,
  });

  const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
    countryCode: 'JP',
    currencyCode: 'JPY',
    merchantCapabilities: ['supports3DS'],
    supportedNetworks: ['visa', 'masterCard', 'amex', 'jcb', 'discover'],
    total: {
      label: process.env.NEXT_PUBLIC_PROVIDER_INFO ?? '',
      type: 'final',
      amount: paymentAmounts.totalPrice.toString(),
    },
  };

  const validateOrderForms = async (): Promise<{
    title: string;
    body: string;
  } | void> => {
    if (isExistingPrecaution && !isConfirmedWarningText) {
      return {
        title: '注意事項にチェックされていません',
        body: '注意事項をご確認の上、チェックボックスにチェックをして同意してください。',
      };
    }

    if (!props.playAmountPlans.includes(props.playAmount)) {
      router.replace(`/e/${props.gachaDetail.unique_name}`);
      return {
        title: 'ガチャ回数が不正です',
        body: '回数選択をやり直してください。',
      };
    }

    if (shouldNotDeliver == false) {
      await deliveryForm.trigger();
      if (props.gachaDetail.delivery_type == 1 && !deliveryFormValues) {
        return {
          title: '郵送先情報が入力されていません',
          body: '郵送先情報を入力してください。',
        };
      }
      const errorObjects = Object.values(deliveryForm.formState.errors);
      if (errorObjects.length > 0) {
        const errorDescription = errorObjects
          .map((error) => `${error.ref?.type}: ${error.message}`)
          .join('\n');
        return {
          title: '郵送先情報の入力に誤りがあります',
          body: errorDescription,
        };
      }
    }
    if (payment_method == null)
      return {
        title: '支払い方法を選択してください',
        body: '支払い方法の欄から選択してください。',
      };
    if (payment_method === 'credit' && card_seq == '') {
      return {
        title: 'クレジットカードが選択されていません',
        body: 'クレジットカードの登録または登録済みのカードから選択してください。',
      };
    }
    if (payment_method == 'play_ticket' && selectedPlayTicket == null) {
      return {
        title: '無料チケットが選択されていません',
        body: 'ギフトコードをお持ちの場合はアカウントに登録をしてください。',
      };
    }
  };

  const parseDeliveryFormValue = (value: DeliveryInfo): DeliveryInfo => {
    return {
      ...value,
      zip_code: value.zip_code.replace(/ /g, '').replace(/-/g, ''),
      phone_number: value.phone_number.replace(/ /g, '').replace(/-/g, ''),
    };
  };

  const handleOrderSubmit = async () => {
    try {
      if (
        tippingMessageForm.watch().tippingMessage.length > MESSAGE_MAX_LENGTH
      ) {
        setPageError({
          title: 'メッセージが多すぎます',
          body: `${MESSAGE_MAX_LENGTH}文字以内で入力してください。`,
        });
        return;
      }
      const validateError = await validateOrderForms();
      if (validateError != undefined) {
        setPageError(validateError);
        return;
      }
      const parsedDeliveryFormValues =
        !shouldNotDeliver && !isLocalPickOnly
          ? parseDeliveryFormValue(deliveryFormValues)
          : null;
      if (payment_method == null) return;

      const applePaySession =
        payment_method === 'applepay'
          ? new ApplePaySession(3, paymentRequest)
          : undefined;

      await props.onOrderSubmit({
        cardSequence: card_seq,
        deliveryInfo: parsedDeliveryFormValues,
        shouldNotDeliver: shouldNotDeliver,
        gachaDetail: props.gachaDetail,
        playAmount: props.playAmount,
        paymentMethod: payment_method,
        carriersPaymentMethod: undefined,
        tippingMessage: tippingMessageForm.watch().tippingMessage,
        applePaySession: applePaySession,
        paymentDetailInput: {
          ...(selectedPlayTicket != null && {
            play_ticket_id: selectedPlayTicket.template_id,
          }),
          ...(payment_method == PAYMENT_METHOD_KV.googlepay && {
            totalPrice: paymentAmounts.totalPrice.toString(),
          }),
        },
      });
      parsedDeliveryFormValues &&
        (await handleUpdateDeliveryInfo(parsedDeliveryFormValues));
    } catch (e) {
      setPageError({
        title: ERROR_DESCRIPTIONS.UNEXPECTED_ERROR.title,
        body:
          e instanceof Error
            ? e.message
            : ERROR_DESCRIPTIONS.UNEXPECTED_ERROR.description,
      });
      Sentry.captureException(e, {
        extra: {
          function: 'PlayGachaFormContainer.handleOrderSubmit',
          gachaDetail: props.gachaDetail,
          playAmount: props.playAmount,
          paymentMethod: payment_method,
          selectedPlayTicket: selectedPlayTicket,
          shouldNotDeliver: shouldNotDeliver,
          paymentDetailInput: {
            ...(selectedPlayTicket != null && {
              play_ticket_id: selectedPlayTicket.template_id,
            }),
            ...(payment_method == PAYMENT_METHOD_KV.googlepay && {
              totalPrice: paymentAmounts.totalPrice.toString(),
            }),
          },
          deliveryFormValues: deliveryFormValues,
        },
      });
    }
  };

  const handleCheckin = async () => {
    if (token == null) return;
    if (!props.gachaDetail.is_required_checkin) {
      return handleOrderSubmit();
    }

    props.gpsCheckinDisclosure.onOpen();
  };

  const handleUpdateDeliveryInfo = async (data: DeliveryInfo) => {
    if (token == null) return;
    await apiClient.v1.me.infos.patch({
      headers: {
        Authorization: `Bearer ${token.token}`,
      },
      body: {
        user_attribute: data,
      },
    });
  };

  return (
    <Flex flexDir="column" w="full" px="16px" gap={8} py={8}>
      <Flex flexDir="row" w="full">
        <Text
          fontSize="22px"
          fontWeight="bold"
          lineHeight="35.42px"
          justifySelf="left"
        >
          {t('pages.buy.title')}
        </Text>
        <Spacer />
      </Flex>
      <Flex
        flexDir="column"
        w="full"
        gap={8}
        display={isLocalPickOnly ? 'none' : 'flex'}
      >
        <SelectProductInfo
          gachaDetail={props.gachaDetail}
          playAmount={props.playAmount}
          deliveryBundles={props.deliveryBundles}
          deliveryInfo={deliveryFormValues}
          deliveryInfoOnOpen={props.deliveryInfoOnOpen}
          skipDelivery={shouldNotDeliver}
          isPlayTicketPayment={selectedPlayTicket != null}
          selectedPlayTicket={selectedPlayTicket}
          bundleAmount={bundleAmount}
          shippingSpec={shippingSpec}
          {...paymentAmounts}
        />
        <NoShipmentSwitch
          canSwitch={props.gachaDetail.allow_no_delivery_order}
          onChange={setShouldNotDeliver}
          isChecked={shouldNotDeliver}
        />
        <SelectDelivery
          shouldNotDeliver={shouldNotDeliver}
          gachaDetail={props.gachaDetail}
          playAmount={props.playAmount}
          deliveryBundles={props.deliveryBundles}
          deliveryInfo={deliveryFormValues}
          canDeliveryInfoEdit={props.canDeliveryInfoEdit}
          canDeliveryForRegisteredAddress={canDeliverableForRegisterAddress}
        />
      </Flex>
      <SelectPaymentMethodContainer
        gachaDetail={props.gachaDetail}
        gachaPaymentMethods={props.gachaPaymentMethods}
        payment_method={payment_method}
        setPaymentMethod={setPaymentMethod}
        setCardSeq={setCardSeq}
        credits={credit.credits}
        loading={credit.loading}
        mutate={credit.mutate}
        playTickets={filteredByPlayAmountPlayTickets}
        setPlayTicket={setPlayTicket}
        selectedPlayTicket={selectedPlayTicket}
      />
      <FormProvider {...tippingMessageForm}>
        <TippingMessageForm form={tippingMessageForm} />
      </FormProvider>
      <SelectProductInfo
        gachaDetail={props.gachaDetail}
        playAmount={props.playAmount}
        deliveryBundles={props.deliveryBundles}
        deliveryInfo={deliveryFormValues}
        deliveryInfoOnOpen={props.deliveryInfoOnOpen}
        skipDelivery={shouldNotDeliver}
        isPlayTicketPayment={selectedPlayTicket != null}
        isLocalPickupOnly={isLocalPickOnly}
        selectedPlayTicket={selectedPlayTicket}
        bundleAmount={bundleAmount}
        shippingSpec={shippingSpec}
        {...paymentAmounts}
      />
      <VStack gap={4} w="full" justifyItems="stretch" alignItems="inherit">
        <SelectWarning
          gachaDetail={props.gachaDetail}
          isConfirmed={isConfirmedWarningText}
          setToConfirm={setToConfirmWarningText}
        />
      </VStack>
      <Center flexDir="column" gap={4}>
        <GachaSalesStatusDisplayButton
          gachaDetail={props.gachaDetail}
          onClick={
            props.gachaDetail.is_checkin ? handleOrderSubmit : handleCheckin
          }
          loading={props.isSubmitting}
        >
          {t('pages.buy.submit_title')}
        </GachaSalesStatusDisplayButton>
        <NextLink href={`/e/${props.gachaDetail.unique_name}`}>
          <Button
            h="32px"
            w="240px"
            variant="ghost"
            color="main"
            loading={props.isSubmitting}
          >
            {t('pages.buy.cancel')}
          </Button>
        </NextLink>
      </Center>
      <GpsCheckinModal
        isOpen={props.gpsCheckinDisclosure.open}
        onClose={props.gpsCheckinDisclosure.onClose}
        onCheckinSuccess={handleOrderSubmit}
        checkinTargetGachaId={props.gachaDetail.id}
      />
    </Flex>
  );
};
